import React, { useState, useEffect } from 'react';
import { Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  BastionAbility,
  BastionFacility,
  BastionItem,
  Gift,
} from "@dndModels";

import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import StringBox from "@components/input/strings/StringBox";
import DescriptionBox from "@dndComponents/DescriptionBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import BastionItemListInput from "@dndComponents/bastion/BastionItemListInput";
import SelectAddableObjectBox from '@components/input/selects/SelectAddableObjectBox';
import DandDAPI from '@utilities/smart_api';

interface Props {
  bf: BastionFacility;
  ability: BastionAbility;
  onChange: () => void; 
  onDelete: () => void; 
  onDone: () => void;
  disabled?: boolean;
}

const api = DandDAPI.getInstance();

export default function BastionAbilityInput({ 
  bf,
  ability,
  onChange,
  onDelete,
  onDone,
  disabled = false,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
     
  }, [reloading]);

  const renderDetails = () => {
    switch (ability.type) {
      case "Research": return renderResearch();
      case "Empower": 
      case "Other": return renderOther();
      case "Craft": 
      case "Harvest": 
      case "Trade": 
      case "Recruit": return renderItems();
    }
  }

  const renderItems = () => {
    return (
      <Grid
        size={{
          xs: 12
        }}>
        <BastionItemListInput 
          bf={bf}
          items={ability.items} 
          onChange={(changed: BastionItem[]) => {
            ability.items = changed;
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderOther = () => {
    return (
      <>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectAddableObjectBox 
            name="Gift"
            data_type="gift"
            values={ability.gift_ids}
            multiple
            onChange={(changed: string[]) => {
              ability.gift_ids = changed;
              onChange();
              setPing(!ping);
            }}
            onCreating={ async (name, onComplete, onError) => {
              const obj = new Gift();
              obj.name = name === "" ? ability.name : name;
              obj.description = ability.description;
              obj.source_books = [...bf.source_books];
              obj.source_books_old = bf.source_books_old.map(o => { return {...o}; });
              const res = await api.createObject("gift", obj);
              if (res && res.body && !res.body.error) {
                obj._id = res.body.id;
                onComplete(obj);
              } else {
                await api.addErrorLog({ file: "BastionAbilityInput.tsx", method: "onCreating", obj: res });
                onError(res);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <StringBox 
            name="Duration (Days)"
            value={`${ability.duration}`}
            type="number"
            onBlur={(changed: string) => {
              ability.duration = +changed;
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            name="Temp HP"
            value={`${ability.temp_hp}`}
            type="number"
            onBlur={(changed: string) => {
              ability.temp_hp = +changed;
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
      </>
    );
  }

  const renderResearch = () => {
    return (
      <>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            name="Takes (Days)"
            value={`${ability.duration}`}
            type="number"
            onBlur={(changed: string) => {
              ability.duration = +changed;
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
      </>
    );
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <Button
          variant="contained"
          color="primary"
          disabled={ ability.name === "" }
          onClick={ () => { 
            onDone();
          }}>
          { bf.name === "" ? `Nameless Bastion Facility` : bf.name }
        </Button>
      </Grid>
      { !disabled &&
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <DeleteButton onClick={onDelete} />
          </Grid>
        </>
      }
      { !reloading &&
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Name"
              value={ability.name}
              disabled={disabled}
              onBlur={(changed: string) => {
                ability.name = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <DescriptionBox
              name="Description"
              value={ability.description}
              disabled={disabled}
              onChange={(changed: string) => {
                ability.description = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Ability Type"
              value={ability.type}
              options={["Craft", "Harvest", "Recruit", "Trade", "Research", "Empower", "Other"]}
              disabled={disabled}
              onChange={(changed: "Craft" | "Harvest" | "Recruit" | "Trade" | "Research" | "Empower" | "Other") => {
                ability.type = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          { renderDetails() }
        </>
      }
    </Grid>
  );
}
